import type {
  LabeledVariants,
  MotionPropsFor,
  SystemProps,
  SystemStyleObject,
} from '@hover/blueprint';
import { mapValues } from '@hover/utilities';
import type React from 'react';

export type PresenceVariants = LabeledVariants<'animate' | 'initial' | 'exit'>;

/**
 * Apply Framer Motion variants to a component using `AnimatePresence`
 *
 * @example
 * ```tsx
 * <motion.div
 *   {...presenceVariants({
 *     animate: { opacity: 1 },
 *     exit: { opacity: 0 },
 *     initial: { opacity: 0 l
 *   })}
 * />;
 * ```
 *
 * @see {@link https://www.framer.com/api/motion/animate-presence/}
 *
 * @param variants
 * @param child whether this component is a child of a component with
 * `presenceVariants` applied (in which case, only the `variants` should be
 * applied and not the variant labels since those should propagate from the
 * parent in that case)
 *
 * @returns motion props to be spread into a component
 */
export const presenceVariants = (
  variants: PresenceVariants,
  child: boolean = false,
) =>
  ({
    variants,
    ...(child ? {} : { animate: 'animate', exit: 'exit', initial: 'initial' }),
  } satisfies MotionPropsFor<SystemProps>);

/**
 * Apply styles to an element that's being animated on unmount using Framer
 * Motion's `AnimatePresence`. Top-level styles are automatically appended
 * with `!important` to override styles Framer applies with `popLayout`.
 *
 * @see {@link https://www.framer.com/api/motion/animate-presence/}
 *
 * @param styles system style object to apply to exiting element
 *
 * @returns styles to be applied to parent element of presence animation
 */
export const exitStyles = (selector: string, styles: SystemStyleObject) => ({
  [`& ${selector}:first-of-type:not(${selector}:last-of-type)`]: {
    ...mapValues(styles, v =>
      typeof v === 'string' || typeof v === 'number' ? `${v} !important` : v,
    ),
  },
});

/**
 * Fade an element wrapped in `AnimatePresence` in and out with `opacity`
 *
 * @example
 * ```tsx
 * <motion.div {...fadePresence} />;
 * ```
 *
 * @returns motion props to be spread into a component
 */
export const fadePresence = presenceVariants({
  animate: { opacity: 1 },
  exit: { opacity: 0 },
  initial: { opacity: 0 },
});

/**
 * Framer-motion enters an awkward drag interaction if the user
 * ctrl+clicks (opens the context menu) inside the element and then left
 * clicks outside the element. The drag interaction becomes stuck "on"
 * even though the user is not holding down the click. We prevent this
 * by calling stopPropagation if the user ctrl+clicks the element
 */
export const fixCtrlClickDrag = (): Pick<
  React.DOMAttributes<any>,
  'onPointerDownCapture'
> => ({
  onPointerDownCapture: event => {
    if (event.ctrlKey) {
      event.stopPropagation();
    }
  },
});
